import { startOfTimeSlot } from '@e2m-customer-portal/shared';
import { Button, FormControlLabel, Switch, SxProps } from '@mui/material';
import { CalendarTodayOutlinedIcon } from 'assets/icons';
import clsx from 'clsx';
import {
  Interval,
  add,
  addMinutes,
  endOfDay,
  getHours,
  getMinutes,
  getSeconds,
  isToday,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import React, { ChangeEvent, JSX, useState } from 'react';
import { AnyState } from 'xstate';
import { DatePicker } from './DatePicker';
import { DateRangePicker } from './DateRangePicker';
import { DayOfWeek, FormatRangeOptions, PickerProps, StateTarget } from './models';
import { TextFieldWithPopover } from './TextFieldWithPopover';
import { TimePicker, TimePickerProps } from './TimePicker';
import { formatRange } from './utils';

interface OnSelectProps {
  date?: Date;
  range: Interval;
}

export interface DateTimePickerProps {
  sx?: SxProps;
  disabled?: boolean;
  required?: boolean;
  hoursStep?: number;
  intradayOffset?: number;
  label: string;
  range?: Interval;
  small?: boolean;
  mode?: 'single' | 'range';
  onCancel?: () => void;
  onSelect?: (props: OnSelectProps) => void;
  value?: Date;
  formats?: {
    range: FormatRangeOptions;
  };
  errorTexts?: {
    timeFromAfterTimeTo: string;
  };
  pickerLabels?: {
    timeLabel?: string;
    timeFromLabel: string;
    timeToLabel: string;
    allDayLabel: string;
    cancelButtonLabel: string;
    submitButtonLabel: string;
  };
  pickerProps?: PickerProps;
  textFieldProps?: {
    value?: string;
    InputProps?: { endAdornment: JSX.Element };
  };
  timePickerProps?: {
    to?: TimePickerProps;
    from?: TimePickerProps;
  };
  validate?: ({ state, range }: { state: AnyState; range: Interval }) => DatePickerError[];
}

export const patterns = {
  connector: ' - ',
  date: 'dd.MM.yyyy',
  time: 'HH:mm',
};

export interface DatePickerError {
  start?: { message: string };
  end?: { message: string };
}

export const defaultPickerProps = {
  dateOnly: false,
  fixedWeeks: true,
  labels: undefined,
  locale: enUS,
  numberOfMonths: 1,
  showWeekNumber: true,
  startOfWeek: 0 as DayOfWeek,
};

export const DateTimePickerBase: React.FC<DateTimePickerProps> = ({
  sx,
  label,
  mode = 'single',
  onCancel = (): void => undefined,
  onSelect = (): void => undefined,
  small,
  hoursStep,
  intradayOffset = 0,
  value: date = new Date(),
  range = { start: date, end: date },
  errorTexts = {
    timeFromAfterTimeTo: 'The start time must be before the end time.',
  },
  pickerLabels = {
    timeLabel: 'Time',
    timeFromLabel: 'From',
    timeToLabel: 'To',
    allDayLabel: 'All-day',
    cancelButtonLabel: 'Cancel',
    submitButtonLabel: 'Submit',
  },
  textFieldProps = {},
  pickerProps = defaultPickerProps,
  timePickerProps = {},
  validate = (): [] => [],
  formats = {
    range: {
      formatter: formatRange,
      patterns: {
        date: patterns.date,
        time: patterns.time,
        connector: patterns.connector,
      },
    },
  },
  ...props
}) => {
  const initialRange = mode === 'single' ? { start: date, end: date } : range;
  const Picker = mode === 'single' ? DatePicker : DateRangePicker;

  const finalTextFieldProps = {
    InputProps: { endAdornment: <CalendarTodayOutlinedIcon className="hidden sm:block" /> },
    label,
    sx: [
      {
        '& .MuiInputBase-root': {
          height: small ? '2.5rem' : undefined,
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      ...(Array.isArray(sx) ? sx : [sx]),
    ],
    value: formats.range.formatter({
      patterns: formats.range.patterns,
      dateOnly: pickerProps.dateOnly,
    })(initialRange as Interval),
    ...props,
    ...textFieldProps,
  };

  const pickerPropsWithInitialValue = {
    defaultMonth: range?.start ?? date,
    ...pickerProps,
    ...(mode === 'single' ? { value: date } : { initialRange }),
  };

  // NOTE If the picker is in 'one-date-selected' mode the end time
  // can be the start of the next day at most (= midnight).
  const isEndTimeInvalid = ({
    state,
    intervalRange,
  }: {
    state: AnyState;
    intervalRange: Interval;
  }): DatePickerError | null => {
    const isInvalid =
      state.value === StateTarget.OneDateSelected &&
      mode !== 'single' &&
      !isWithinInterval(intervalRange.end, {
        start: add(intervalRange.start, { seconds: 1 }),
        end: add(endOfDay(intervalRange.start), { seconds: 1 }),
      });

    return isInvalid ? { end: { message: errorTexts.timeFromAfterTimeTo } } : null;
  };

  const [allDayEnabled, setAllDayEnabled] = useState(false);

  const allDayRange = (pickerRange: Interval): Interval => {
    if (!allDayEnabled) return pickerRange;

    pickerRange.start = isToday(pickerRange.start)
      ? startOfTimeSlot(addMinutes(new Date(), intradayOffset))
      : startOfDay(pickerRange.start);
    if (pickerRange.end > startOfDay(pickerRange.end) || pickerRange.end < startOfDay(pickerRange.end))
      pickerRange.end = add(startOfDay(pickerRange.end), { days: 1 });

    return pickerRange;
  };

  return (
    <TextFieldWithPopover {...finalTextFieldProps}>
      {({ handleClose }) => (
        <Picker {...pickerPropsWithInitialValue}>
          {({
            send,
            state,
            date: pickerDate,
            range: pickerRange,
          }: {
            send: (type: string, data: object) => void;
            state: AnyState;
            date?: Date;
            range: Interval;
          }) => {
            pickerRange = allDayRange(pickerRange);

            const errors = [
              !pickerProps.dateOnly ? isEndTimeInvalid({ state, intervalRange: pickerRange }) : undefined,
              ...validate({ state, range: pickerRange }),
            ].filter(Boolean);

            // NOTE We'll use the first error only to avoid overwhelming the user with messages
            const startError = errors.find((error) => error?.start);
            const endError = errors.find((error) => error?.end);

            return (
              <div>
                {!pickerProps.dateOnly && (
                  <ol
                    className={clsx('m-0 list-none flex justify-center gap-x-1 gap-y-2 p-0', {
                      'flex-wrap': pickerProps.numberOfMonths && pickerProps.numberOfMonths <= 1,
                    })}
                  >
                    {mode !== 'single' && (
                      <li
                        className={clsx(
                          pickerProps.numberOfMonths && pickerProps.numberOfMonths <= 1 ? 'p-0 pb-6' : 'px-1 py-0',
                        )}
                      >
                        <FormControlLabel
                          className="m-0 flex items-center gap-2 leading-none"
                          control={
                            <Switch
                              checked={allDayEnabled}
                              onChange={(event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
                                setAllDayEnabled(checked)
                              }
                            />
                          }
                          label={pickerLabels.allDayLabel}
                        />
                      </li>
                    )}
                    <li className="max-w-40 px-1 py-0">
                      <TimePicker
                        error={startError?.start?.message}
                        label={mode === 'single' ? pickerLabels.timeLabel : pickerLabels.timeFromLabel}
                        value={pickerRange.start}
                        hoursStep={hoursStep}
                        disabled={allDayEnabled}
                        onChange={({ value: nextDate }) =>
                          send('change:start-time', {
                            hours: getHours(nextDate),
                            minutes: getMinutes(nextDate),
                            seconds: getSeconds(nextDate),
                          })
                        }
                        {...timePickerProps?.from}
                      />
                    </li>
                    {mode !== 'single' && (
                      <>
                        <li className="flex h-10 w-3 items-center justify-center">
                          <div className="w-[80%] h-px bg-black" />
                        </li>
                        <li className="max-w-40 px-1 py-0">
                          <TimePicker
                            error={endError?.end?.message}
                            label={pickerLabels.timeToLabel}
                            value={pickerRange.end}
                            hoursStep={hoursStep}
                            disabled={allDayEnabled}
                            onChange={({ value: nextDate }) =>
                              send('change:end-time', {
                                hours: getHours(nextDate),
                                minutes: getMinutes(nextDate),
                                seconds: getSeconds(nextDate),
                              })
                            }
                            {...timePickerProps?.to}
                          />
                        </li>
                      </>
                    )}
                  </ol>
                )}
                <footer className="mx-1 mt-5 flex justify-between border-0 border-t border-solid border-grey-100 pb-2 pt-5">
                  <Button
                    aria-label={pickerLabels.cancelButtonLabel}
                    color="primary"
                    onClick={() => {
                      onCancel?.();
                      handleClose();
                    }}
                    variant="outlined"
                  >
                    {pickerLabels.cancelButtonLabel}
                  </Button>
                  <Button
                    aria-label={pickerLabels.submitButtonLabel}
                    color="primary"
                    disabled={errors.length > 0}
                    onClick={() => {
                      onSelect({ range: pickerRange, ...(pickerDate && { date: pickerDate }) });
                      handleClose();
                    }}
                  >
                    {pickerLabels.submitButtonLabel}
                  </Button>
                </footer>
              </div>
            );
          }}
        </Picker>
      )}
    </TextFieldWithPopover>
  );
};
